import type { FC, ReactNode } from 'react'
import type { TagKind, TagWithCount } from '@/types'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCount } from '@/lib/format'
import { CATEGORY_ORDER, TAG_KINDS, categoryLabel, tagHref } from '@/lib/kinds'
import PageHeader from '@/components/kb/page-header'
import EmptyState from '@/components/kb/empty-state'
import { Pill } from '@/components/kb/section-heading'

interface TagIndexViewProps {
    kind: TagKind
    tags: TagWithCount[]
    unavailable: boolean
    title: string
    hindi: string
    description: string
    children?: ReactNode
}

type SortMode = 'popular' | 'az'

const TagIndexView: FC<TagIndexViewProps> = ({ kind, tags, unavailable, title, hindi, description, children }): ReactNode => {
    const meta = TAG_KINDS[kind]
    const Icon = meta.icon
    const [filter, setFilter] = useState('')
    const [sort, setSort] = useState<SortMode>('popular')

    const groups = useMemo(() => {
        const term = filter.trim().toLowerCase()
        const visible = term
            ? tags.filter(tag => tag.name.toLowerCase().includes(term) || (tag.name_hi ?? '').includes(term) || tag.slug.includes(term.replace(/\s+/g, '-')))
            : tags

        const sorted = [...visible].sort((a, b) => sort === 'popular' ? (b.entry_count - a.entry_count || a.name.localeCompare(b.name)) : a.name.localeCompare(b.name))
        const byCategory = new Map<string, TagWithCount[]>()

        for (const tag of sorted) {
            const key = tag.category ?? 'other'

            byCategory.set(key, [...(byCategory.get(key) ?? []), tag])
        }

        const order = CATEGORY_ORDER[kind]
        const keys = [...order.filter(key => byCategory.has(key)), ...[...byCategory.keys()].filter(key => !order.includes(key))]

        return keys.map(key => ({ category: key, tags: byCategory.get(key) ?? [] }))
    }, [filter, sort, tags, kind])

    const used = tags.filter(tag => tag.entry_count > 0).length

    return (
        <>
            <PageHeader
                crumbs={[{ label: 'Home', href: '/' }, { label: meta.label }]}
                eyebrow={(
                    <Pill tone={meta.tone}>
                        <Icon className='size-4' aria-hidden='true' />
                        {hindi}
                    </Pill>
                )}
                title={title}
                description={description}
            >
                <div className='flex max-w-2xl flex-col gap-3 sm:flex-row'>
                    <label htmlFor={`${kind}-filter`} className='sr-only'>
                        Filter {meta.label.toLowerCase()}
                    </label>

                    <div className='relative flex-1'>
                        <Search className='pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2' aria-hidden='true' />

                        <input
                            id={`${kind}-filter`}
                            type='search'
                            value={filter}
                            onChange={event => setFilter(event.target.value)}
                            placeholder={`Filter ${formatCount(tags.length)} ${meta.label.toLowerCase()}`}
                            className='h-12 w-full rounded-xl border-2 border-foreground bg-card pl-10 pr-3 text-sm font-medium shadow-brutal outline-none'
                        />
                    </div>

                    <div className='flex h-12 items-center gap-1 rounded-xl border-2 border-foreground bg-card p-1 text-xs font-bold shadow-brutal'>
                        <button type='button' onClick={() => setSort('popular')} className={cn('h-full rounded-lg px-3', sort === 'popular' ? 'bg-primary' : 'hover:bg-secondary')} aria-pressed={sort === 'popular'}>
                            Most used
                        </button>

                        <button type='button' onClick={() => setSort('az')} className={cn('h-full rounded-lg px-3', sort === 'az' ? 'bg-primary' : 'hover:bg-secondary')} aria-pressed={sort === 'az'}>
                            A to Z
                        </button>
                    </div>
                </div>

                <p className='mt-4 font-mono text-xs font-bold text-muted-foreground'>
                    {formatCount(used)} of {formatCount(tags.length)} have linked entries
                </p>
            </PageHeader>

            {children}

            <div className='mx-auto max-w-6xl px-4 py-10 sm:px-6'>
                {unavailable && <EmptyState title='Could not load this list' text='Please try again in a moment.' />}

                {!unavailable && groups.length === 0 && (
                    <EmptyState title='No match' text='Try a shorter word or another spelling, for example sarso instead of sarson.' />
                )}

                <div className='flex flex-col gap-12'>
                    {groups.map(group => (
                        <section key={group.category} aria-labelledby={`group-${group.category}`}>
                            <h2 id={`group-${group.category}`} className='mb-4 flex items-center gap-3 text-xl font-black md:text-2xl'>
                                {categoryLabel(group.category)}
                                <span className='rounded-md border-2 border-foreground px-2 py-0.5 font-mono text-xs'>
                                    {group.tags.length}
                                </span>
                            </h2>

                            <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                                {group.tags.map(tag => (
                                    <Link
                                        key={tag.id}
                                        href={tagHref(tag.kind, tag.slug)}
                                        className={cn(
                                            'group flex items-center justify-between gap-3 rounded-xl border-2 border-foreground bg-card px-4 py-3 shadow-brutal-sm transition-colors hover:bg-primary',
                                            tag.entry_count === 0 && 'opacity-60'
                                        )}
                                    >
                                        <span className='min-w-0'>
                                            <span className='block truncate font-bold' data-mdlfy-cms={`tags:${tag.id}:name`}>
                                                {tag.name}
                                            </span>

                                            {tag.name_hi && (
                                                <span className='block truncate text-xs text-muted-foreground group-hover:text-foreground' data-mdlfy-cms={`tags:${tag.id}:name_hi`}>
                                                    {tag.name_hi}
                                                </span>
                                            )}
                                        </span>

                                        <span className='shrink-0 text-right font-mono text-xs font-bold'>
                                            {formatCount(tag.entry_count)}
                                            <span className='block text-[10px] font-medium text-muted-foreground group-hover:text-foreground'>
                                                {tag.qa_count > 0 ? `+${formatCount(tag.qa_count)} Q&A` : 'uses'}
                                            </span>
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </>
    )
}

export default TagIndexView
